import React, {useState, useEffect, useLayoutEffect, useRef, useMemo} from 'react';
import {useNavigate, useLocation} from 'react-router-dom';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import styles from '../designs/FootballPage.module.css';
import englandFlag from '../designs/flags/england.png';
import spainFlag from '../designs/flags/spain.png';
import italyFlag from '../designs/flags/italy.png';
import deutschlandFlag from '../designs/flags/deutschland.png';
import turkeyFlag from '../designs/flags/turkey.png';
import uclFlag from '../designs/flags/ucl.png';
import ukraineFlag from '../designs/flags/ukraine.png';
import ueclFlag from '../designs/flags/uecl.png';
import uelFlag from '../designs/flags/uel.png';
import franceFlag from '../designs/flags/france.png';
import WCFlag from '../designs/flags/wc.png';
import NLFlag from '../designs/flags/nl.png';
import placeholderLogo from '../designs/placeholder.png';
import {useUser} from './UserContext';
import {isDemoBetUsedLocal, isDemoModeEnabled} from '../utils/demoAccess';

const SELECTED_DATE_KEY = 'selectedDate';
const SELECTED_DATE_INDEX_KEY = 'selectedDateIndex';
const SELECTED_DATE_SAVED_AT_KEY = 'selectedDateSavedAt';
const SELECTED_DATE_TTL_MS = 60 * 60 * 1000;

function FootballPage() {
    const [matches, setMatches] = useState([]);
    const [matchesLoading, setMatchesLoading] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();
    const [selectedDate, setSelectedDate] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [manualReloading, setManualReloading] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [sortOrder, setSortOrder] = useState('alphabet');
    const {user} = useUser();
    const [isDemoMode] = useState(() => isDemoModeEnabled());
    const [demoStatus, setDemoStatus] = useState(null);
    const demoActive = isDemoMode && !user?.has_access;
    const sliderRef = useRef(null);
    const containerRef = useRef(null);
    const matchesRequestRef = useRef(0);

    const toLocalISODate = (d) => {
        // Convert a Date to YYYY-MM-DD in local timezone (not UTC)
        const tzOffsetMs = d.getTimezoneOffset() * 60 * 1000;
        return new Date(d.getTime() - tzOffsetMs).toISOString().split('T')[0];
    };

    const getTodayLocal = () => toLocalISODate(new Date());

    const rememberSelectedDate = (date, index = null) => {
        if (!date) return;
        localStorage.setItem(SELECTED_DATE_KEY, date);
        localStorage.setItem(SELECTED_DATE_SAVED_AT_KEY, String(Date.now()));
        if (index !== null && index !== undefined) {
            localStorage.setItem(SELECTED_DATE_INDEX_KEY, String(index));
        }
    };

    const forgetSelectedDate = () => {
        localStorage.removeItem(SELECTED_DATE_KEY);
        localStorage.removeItem(SELECTED_DATE_INDEX_KEY);
        localStorage.removeItem(SELECTED_DATE_SAVED_AT_KEY);
    };

    const getFreshSavedDate = () => {
        const savedDate = localStorage.getItem(SELECTED_DATE_KEY);
        const savedAt = Number(localStorage.getItem(SELECTED_DATE_SAVED_AT_KEY) || 0);
        if (!savedDate || !savedAt || Date.now() - savedAt > SELECTED_DATE_TTL_MS) {
            forgetSelectedDate();
            return null;
        }
        return savedDate;
    };

    const flagMap = {
        ENG: englandFlag,
        ESP: spainFlag,
        ITA: italyFlag,
        GER: deutschlandFlag,
        TR: turkeyFlag,
        UCL: uclFlag,
        UEL: uelFlag,
        UECL: ueclFlag,
        UA: ukraineFlag,
        FRN: franceFlag,
        WC: WCFlag,
        NL: NLFlag,
    };

    const listRef = useRef(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const [listAnimationKey, setListAnimationKey] = useState(0);
    const scrollingRef = useRef(false);

    // ✅ When coming back to this route (especially from deep scroll), some WebViews skip a paint.
    // We safely retrigger paint + remount of the content without scroll nudges.
    useLayoutEffect(() => {
        // Remount content container to restart any CSS entry animations safely.
        setRefreshKey((k) => k + 1);

        const el = containerRef.current;
        if (!el) return;

        // Force a synchronous layout read + a stable compositing layer.
        // DO NOT toggle it off (toggling can cause black flashes on some WebViews).
        el.style.transform = 'translate3d(0,0,0)';
        el.style.backfaceVisibility = 'hidden';
        // eslint-disable-next-line no-unused-expressions
        void el.offsetHeight;

        // Next frame: one more layout read to ensure paint.
        const raf = requestAnimationFrame(() => {
            // eslint-disable-next-line no-unused-expressions
            void el.getBoundingClientRect();
        })
        return () => cancelAnimationFrame(raf);
    }, [location.key]);

    // 🔁 слухаємо звичайний window scroll, а не внутрішній div
    useEffect(() => {
        const el = window;
        const onScroll = () => {
            scrollingRef.current = true;
            clearTimeout(onScroll._t);
            onScroll._t = setTimeout(() => (scrollingRef.current = false), 150);
        };
        el.addEventListener('scroll', onScroll, {passive: true});
        return () => {
            el.removeEventListener('scroll', onScroll);
            clearTimeout(onScroll._t);
        };
    }, []);

    // періодичне оновлення матчів
    useEffect(() => {
        if (!selectedDate) return;
        const tick = async () => {
            if (!scrollingRef.current && document.visibilityState === 'visible') {
                await loadMatches(selectedDate);
            }
        };
        const id = setInterval(tick, 60000);
        return () => clearInterval(id);
    }, [selectedDate]);
    // 👑 Admin detection: check role in DB via backend (/api/user/{telegram_id})
    useEffect(() => {
        let cancelled = false;

        const detect = async () => {
            try {
                const tgId = window?.Telegram?.WebApp?.initDataUnsafe?.user?.id;
                const lsTelegramId = localStorage.getItem('telegram_id');
                const telegramId = tgId || (lsTelegramId ? Number(lsTelegramId) : null);

                if (!telegramId) {
                    // fallback: if you store role locally
                    const lsRole = (localStorage.getItem('role') || '').toLowerCase();
                    if (!cancelled) setIsAdmin(lsRole === 'admin');
                    return;
                }

                const API_BASE = process.env.REACT_APP_API_BASE || '/api';
                const res = await fetch(`${API_BASE}/user/${telegramId}`);
                const data = await res.json().catch(() => null);

                // backend returns either the user object directly OR {user: {...}}
                const user = data?.user || data;
                const role = (user?.role || '').toLowerCase();

                if (!cancelled) {
                    setIsAdmin(role === 'admin');
                    // keep a light cache for fallback usage
                    if (role) localStorage.setItem('role', role);
                    localStorage.setItem('telegram_id', String(telegramId));
                }
            } catch (e) {
                // fallback to local role if request fails
                const lsRole = (localStorage.getItem('role') || '').toLowerCase();
                if (!cancelled) setIsAdmin(lsRole === 'admin');
            }
        };

        detect();
        return () => {
            cancelled = true;
        };
    }, []);


    useEffect(() => {
        const savedDate = demoActive ? null : getFreshSavedDate();
        const savedCountry = localStorage.getItem('selectedCountry');
        const savedIndex = parseInt(
            localStorage.getItem(SELECTED_DATE_INDEX_KEY) || '0',
            10
        );
        const today = getTodayLocal();

        const initialDates = getFutureDates();

        // Keep the last opened date while it is still available in the local date scroller.
        // This lets users return from a match to the same day, including recent past dates.
        const useSaved = !!savedDate && initialDates.includes(savedDate);
        const initialDate = useSaved ? savedDate : today;
        const initialIndex = initialDates.indexOf(initialDate);

        setSelectedDate(initialDate);
        setSelectedCountry(savedCountry || null);
        rememberSelectedDate(initialDate, initialIndex >= 0 ? initialIndex : 0);

        loadMatches(initialDate, false, true);

        const timer = setTimeout(() => {
            if (sliderRef.current) {
                const el = sliderRef.current.children?.[initialIndex >= 0 ? initialIndex : 0];
                el?.scrollIntoView({
                    behavior: 'auto',
                    inline: 'center',
                    block: 'nearest',
                });
                console.log(`Прокручено до індексу: ${initialIndex}`);
            } else {
                console.error('sliderRef.current не ініціалізований');
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [demoActive]);

    useEffect(() => {
        if (!demoActive || !selectedDate) return;

        const telegramId = user?.telegram_id || window.Telegram?.WebApp?.initDataUnsafe?.user?.id || localStorage.getItem('telegram_id');
        if (!telegramId) return;

        let cancelled = false;
        const loadDemoStatus = async () => {
            try {
                const API_BASE = process.env.REACT_APP_API_BASE || '/api';
                const qs = new URLSearchParams({telegram_id: String(telegramId), date: selectedDate});
                const res = await fetch(`${API_BASE}/demo-access/status?${qs.toString()}&_=${Date.now()}`, {
                    headers: {'Cache-Control': 'no-cache'},
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                if (!cancelled) setDemoStatus(data);
            } catch (error) {
                console.error('Demo status error:', error.message);
                if (!cancelled) setDemoStatus({demo: true, used: false, allowed_fixture_id: null});
            }
        };

        loadDemoStatus();
        return () => {
            cancelled = true;
        };
    }, [demoActive, selectedDate, user?.telegram_id]);


    useEffect(() => {
        const tg = window?.Telegram?.WebApp;
        try {
            tg?.ready?.();
            tg?.expand?.();
            tg?.disableVerticalSwipes?.();
        } catch {
        }

        return () => {
            try {
                tg?.disableVerticalSwipes?.();
            } catch {
            }
        };
    }, []);

    useEffect(() => {
        if (selectedCountry) {
            localStorage.setItem('selectedCountry', selectedCountry);
        } else {
            localStorage.removeItem('selectedCountry');
        }
    }, [selectedCountry]);


    // (Removed duplicate periodic refresh effect)
    const handleManualReload = async () => {
        if (!selectedDate) return;
        try {
            setManualReloading(true);
            await loadMatches(selectedDate, true);
            // force a light remount for some WebViews
            setRefreshKey((k) => k + 1);
        } finally {
            setManualReloading(false);
        }
    };

    const handleClick = (match) => {
        try {
            if (demoActive && !isDemoMatchUnlocked(match)) {
                window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred?.('warning');
                return;
            }
            if (!match.home_team || !match.away_team || !match.country) {
                console.error('Некоректні дані матчу:', match);
                return;
            }
            console.log(
                `Перехід до матчу: home_logo=${match.home_logo}, away_logo=${match.away_logo}`
            );
            rememberSelectedDate(selectedDate);
            if (selectedCountry) {
                localStorage.setItem('selectedCountry', selectedCountry);
            } else {
                localStorage.removeItem('selectedCountry');
            }
            navigate(
                `/match?home_team=${encodeURIComponent(
                    match.home_team
                )}&away_team=${encodeURIComponent(
                    match.away_team
                )}&date=${encodeURIComponent(
                    selectedDate
                )}&country=${encodeURIComponent(
                    match.country
                )}&home_logo=${encodeURIComponent(
                    match.home_logo || ''
                )}&away_logo=${encodeURIComponent(
                    match.away_logo || ''
                )}&status=${encodeURIComponent(
                    match.status || 'NS'
                )}&score=${encodeURIComponent(
                    match.score || ''
                )}&elapsed=${encodeURIComponent(
                    match.elapsed != null && match.elapsed !== ''
                        ? match.elapsed
                        : ''
                )}&time=${encodeURIComponent(match.time || '')}`
                + `${match.fixture_id ? `&fixture_id=${encodeURIComponent(match.fixture_id)}` : ''}`
                + `${match.league_id ? `&league_id=${encodeURIComponent(match.league_id)}` : ''}`
            );
        } catch (e) {
            console.error('Помилка при навігації:', e.message);
        }
    };

    const loadMatches = async (date, force = false, clearPrevious = false) => {
        const targetDate = date || getTodayLocal();
        const requestId = ++matchesRequestRef.current;

        if (clearPrevious) {
            setMatches([]);
            setMatchesLoading(true);
        }

        console.log(
            `Завантаження матчів для дати: ${targetDate} at ${new Date().toLocaleTimeString()}`
        );
        try {
            const API_BASE = process.env.REACT_APP_API_BASE || '/api';
            const qs = new URLSearchParams({date: targetDate});
            if (force) qs.set('force', '1');

            const res = await fetch(`${API_BASE}/get_matches?${qs.toString()}`, {
                method: 'GET',
            });
            console.log(`Response status: ${res.status}, OK: ${res.ok}`);
            const data = await res.json();
            console.log(
                'Дані від API:',
                JSON.stringify(
                    data.matches.map((m) => ({
                        home_team: m.home_team,
                        away_team: m.away_team,
                        score: m.score,
                        status: m.status,
                        time: m.time,
                        elapsed: m.elapsed,
                    })),
                    null,
                    2
                )
            );
            if (data.error) {
                console.error('Помилка від API:', data.error);
                return;
            }
            if (!Array.isArray(data.matches)) {
                console.error('Очікувався масив matches, отримано:', data);
                return;
            }
            console.log(`Отримано ${data.matches.length} матчів`);
            if (requestId === matchesRequestRef.current) {
                setMatches(data.matches || []);
                setSelectedDate(targetDate);
                rememberSelectedDate(targetDate);
            }
        } catch (e) {
            console.error('Помилка при завантаженні матчів:', e.message);
        } finally {
            if (requestId === matchesRequestRef.current) {
                setMatchesLoading(false);
            }
        }
    };

    const getFutureDates = () => {
        const dates = [];
        const today = new Date();
        for (let i = -3; i <= 10; i++) {
            const date = new Date(today);
            date.setDate(today.getDate() + i);
            dates.push(toLocalISODate(date));
        }
        return dates;
    };

    const isDateLockedForDemo = (date) => demoActive && date !== getTodayLocal();

    const getDemoLeagueWeight = (match) => {
        const lid = String(match?.league_id || '');
        const league = String(match?.league || match?.league_name || '').toLowerCase();
        const byId = {
            2: 100, 1: 98, 39: 96, 140: 94, 135: 92, 78: 90,
            3: 88, 848: 84, 61: 82, 203: 78, 333: 72,
            48: 70, 143: 70, 137: 70,
        };
        if (byId[lid]) return byId[lid];
        if (league.includes('champions league')) return 100;
        if (league.includes('world cup')) return 98;
        if (league.includes('premier league')) return 96;
        if (league.includes('la liga')) return 94;
        if (league.includes('serie a')) return 92;
        if (league.includes('bundesliga')) return 90;
        if (league.includes('europa league')) return 88;
        return 50;
    };

    const getDemoMatchKey = (match) => [
        String(match?.home_team || '').trim().toLowerCase(),
        String(match?.away_team || '').trim().toLowerCase(),
    ].join('|');

    const demoBetUsed = demoActive && (!!demoStatus?.used || isDemoBetUsedLocal());

    const localDemoAllowedMatch = useMemo(() => {
        if (!demoActive || demoBetUsed) return null;
        if (demoStatus?.allowed_match) {
            return {
                fixtureId: demoStatus.allowed_fixture_id ? String(demoStatus.allowed_fixture_id) : '',
                matchKey: getDemoMatchKey(demoStatus.allowed_match),
            };
        }
        if (demoStatus?.allowed_fixture_id) {
            return {
                fixtureId: String(demoStatus.allowed_fixture_id),
                matchKey: '',
            };
        }
        const finalStatuses = new Set(['FT', 'AET', 'PEN', 'AWD', 'ABD', 'CANC', 'SUSP', 'WO', 'INT', 'PPD', 'PST']);
        const candidates = matches
            .filter((match) => !finalStatuses.has(String(match?.status || 'NS').toUpperCase()));
        if (!candidates.length) return null;
        candidates.sort((a, b) => (
            getDemoLeagueWeight(a) - getDemoLeagueWeight(b) ||
            String(a.time || '99:99').localeCompare(String(b.time || '99:99')) ||
            Number(a.fixture_id || 0) - Number(b.fixture_id || 0)
        ));
        return {
            fixtureId: candidates[0].fixture_id ? String(candidates[0].fixture_id) : '',
            matchKey: getDemoMatchKey(candidates[0]),
        };
    }, [demoActive, demoBetUsed, demoStatus?.allowed_fixture_id, demoStatus?.allowed_match, matches]);

    const isDemoMatchUnlocked = (match) => {
        if (!demoActive) return true;
        if (demoBetUsed) return false;
        if (!localDemoAllowedMatch) return false;

        const fixtureId = String(match?.fixture_id || '');
        if (fixtureId && localDemoAllowedMatch.fixtureId) {
            return fixtureId === localDemoAllowedMatch.fixtureId;
        }
        return getDemoMatchKey(match) === localDemoAllowedMatch.matchKey;
    };

    const getUniqueCountries = () => {
        const countries = matches.map((match) => match.country).filter(Boolean);
        return [...new Set(countries)];
    };

    const isLiveMatch = (match) => {
        const status = String(match?.status || '').toUpperCase();
        return ['LIVE', '1H', '2H', 'HT', 'ET', 'P'].includes(status);
    };

    const filteredMatches = selectedCountry === 'LIVE'
        ? matches.filter((match) => isLiveMatch(match))
        : selectedCountry
            ? matches.filter((match) => match.country === selectedCountry)
            : matches;

    const sortedMatches = [...filteredMatches].sort((a, b) => {
        if (sortOrder === 'time') {
            return (a.time || '').localeCompare(b.time || '');
        }
        return 0;
    });

    const groupedMatches = useMemo(() => {
        const groupsMap = new Map();
        const leagueNameCounts = sortedMatches.reduce((acc, match) => {
            const leagueName = match.league || match.league_name || match.country || 'Інші матчі';
            acc.set(leagueName, (acc.get(leagueName) || 0) + 1);
            return acc;
        }, new Map());

        sortedMatches.forEach((match) => {
            const leagueName =
                match.league ||
                match.league_name ||
                match.country ||
                'Інші матчі';
            const leagueId = String(match.league_id || '').trim();
            const country = String(match.country || '').trim();
            const groupKey = [
                leagueId || leagueName,
                country || 'unknown',
                leagueName,
            ].join('|');
            const shouldShowCountry = country && leagueNameCounts.get(leagueName) > 1;
            const displayLeagueName = shouldShowCountry
                ? `${leagueName} · ${country}`
                : leagueName;

            if (!groupsMap.has(groupKey)) {
                groupsMap.set(groupKey, {
                    groupKey,
                    leagueName: displayLeagueName,
                    matches: [],
                });
            }
            groupsMap.get(groupKey).matches.push(match);
        });

        return Array.from(groupsMap.values());
    }, [sortedMatches]);

    const animatedGroups = useMemo(() => {
        let matchOffset = 0;

        return groupedMatches.map((group) => {
            const animatedGroup = {
                ...group,
                animationOffset: matchOffset,
            };
            matchOffset += group.matches.length;
            return animatedGroup;
        });
    }, [groupedMatches]);

    const selectCountryFilter = (country) => {
        setSelectedCountry(country);
        setListAnimationKey((key) => key + 1);
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        const day = date.getDate();
        const month = date.toLocaleString('default', {month: 'long'});
        const weekday = date.toLocaleString('default', {weekday: 'long'});
        return `${weekday}, ${day} ${month}`;
    };

    const hasLiveMatches = matches.some((match) => isLiveMatch(match));
    const liveMatchesCount = matches.filter((match) => isLiveMatch(match)).length;
    const dates = getFutureDates();

    return (
        <div
            ref={containerRef}
            className={styles.container}
            style={{overflowX: 'hidden'}}
        >
            {/* Фон-градієнт */}
            <div className={styles.animatedGradient}></div>

            {/* Кнопка назад (плаває зверху) */}
            <div className={styles.backButtonContainer}>
                <button className={styles.backButton} onClick={() => navigate(-1)}>
                    Назад
                </button>

                {isAdmin && (
                    <button
                        className={styles.backButton}
                        onClick={handleManualReload}
                        disabled={manualReloading}
                        style={{marginLeft: 10, opacity: manualReloading ? 0.7 : 1}}
                    >
                        {manualReloading ? 'Оновлення…' : 'Перезавантажити'}
                    </button>
                )}
            </div>

            <header className={styles.pageHero}>
                <div className={styles.pageEyebrow}>Grach Match Center</div>
                <h1 className={styles.pageTitle}>УСІ <span>МАТЧІ</span></h1>
                <div className={styles.pageSummary}>
                    <span><strong>{matches.length}</strong> у розкладі</span>
                    <span className={styles.summaryDivider}/>
                    <span className={liveMatchesCount > 0 ? styles.liveSummary : ''}>
                        <strong>{liveMatchesCount}</strong> наживо
                    </span>
                </div>
            </header>


            {/* Карусель дат */}
            <div
                className={styles.sliderContainer}
                onTouchStart={(e) => e.stopPropagation()}
                onTouchMove={(e) => e.stopPropagation()}
                onTouchEnd={(e) => e.stopPropagation()}
            >
                <div className={styles.dateScroller} ref={sliderRef}>
                    {dates.map((date, idx) => {
                        return (
                            <div key={date} className={styles.dateButtonWrapper}>
                                <button
                                    className={`${styles.dateButton} ${
                                        selectedDate === date
                                            ? styles.dateButtonSelected
                                            : ''
                                    } ${isDateLockedForDemo(date) ? styles.dateButtonLocked : ''}`}
                                    disabled={isDateLockedForDemo(date)}
                                    onClick={() => {
                                        if (isDateLockedForDemo(date)) return;
                                        const savedIndex = dates.indexOf(date);
                                        if (savedIndex >= 0) {
                                            rememberSelectedDate(date, savedIndex);
                                            localStorage.removeItem('selectedCountry');
                                            setSelectedCountry(null);
                                            setSelectedDate(date);
                                            setListAnimationKey((key) => key + 1);
                                            loadMatches(date, false, true);

                                            const el = sliderRef.current?.children?.[savedIndex];
                                            el?.scrollIntoView({
                                                behavior: 'smooth',
                                                inline: 'center',
                                                block: 'nearest',
                                            });

                                            console.log(
                                                `Клікнуто дату ${date}, індекс: ${savedIndex}`
                                            );
                                        } else {
                                            console.error(
                                                `Дата ${date} не знайдена в dates`
                                            );
                                        }
                                    }}
                                >
                                    {(() => {
                                        const d = new Date(date);
                                        const isToday = idx === 3;

                                        const firstLine = isToday
                                            ? '🗓️ Сьогодні'
                                            : `🗓️ ${d.getDate()} ${d.toLocaleString('default', {month: 'long'})}`;

                                        const secondLine = d.toLocaleString('default', {weekday: 'long'});

                                        return (
                                            <>
                                                <span className={styles.dateButtonLinePrimary}>{firstLine}</span>
                                                <span className={styles.dateButtonLineSecondary}>{secondLine}</span>
                                            </>
                                        );
                                    })()}
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Основний контент — тепер звичайний, скролиться window-ом */}
            <div ref={listRef} key={refreshKey} className={styles.contentContainer}>
                {demoActive && (
                    <div className={styles.demoNotice}>
                        {demoStatus?.used
                            ? 'Демо-ставку вже використано. Повний доступ відкриє всі матчі.'
                            : 'Демо-режим: сьогодні відкритий тільки один матч і одна ставка.'}
                    </div>
                )}
                {/* Фільтр по країнах */}
                <div className={styles.buttonGroup}>
                    <button
                        onClick={() => selectCountryFilter(null)}
                        className={`${styles.beautifulButton} ${
                            selectedCountry === null ? styles.beautifulButtonSelected : ''
                        }`}
                    >
                        🌐 Усі матчі
                    </button>

                    {hasLiveMatches && (
                        <button
                            onClick={() => selectCountryFilter('LIVE')}
                            className={`${styles.beautifulButton} ${
                                selectedCountry === 'LIVE' ? styles.beautifulButtonSelected : ''
                            }`}
                        >
                            🔴 Лайв
                        </button>
                    )}

                    {getUniqueCountries().map((country) => (
                        <button
                            key={country}
                            onClick={() => selectCountryFilter(country)}
                            className={`${styles.beautifulButton} ${
                                selectedCountry === country
                                    ? styles.beautifulButtonSelected
                                    : ''
                            }`}
                        >
                            <img
                                src={flagMap[country.toUpperCase()] || placeholderLogo}
                                alt={country || 'Невідома країна'}
                                style={{
                                    width: '24px',
                                    height: '18px',
                                    marginRight: '8px',
                                    objectFit: 'contain',
                                }}
                            />
                            {country}
                        </button>
                    ))}
                </div>

                {/* Список матчів */}
                <div
                    key={`matches-${listAnimationKey}`}
                    className={styles.matchesContainer}
                >
                    {matchesLoading ? (
                        <div className={styles.matchesLoading} role="status">
                            <span/>
                            <span/>
                            <span/>
                            <strong>Завантажуємо матчі</strong>
                        </div>
                    ) : animatedGroups.length > 0 ? (
                        animatedGroups.map((group) => (
                            <div
                                key={group.groupKey}
                                className={styles.leagueSection}
                            >
                                <div className={styles.leagueHeader}>
                                    <span className={styles.leagueMarker}/>
                                    {group.leagueName}
                                </div>

                                <div className={styles.leagueMatchesList}>
                                    {group.matches.map((match, matchIndex) => {
                                        const stableKey = [
                                            selectedDate,
                                            group.groupKey,
                                            match.home_team,
                                            match.away_team,
                                            match.time || '',
                                        ].join('|');

                                        const isNotStarted = (match.status || 'NS') === 'NS';
                                        const displayText = isNotStarted
                                            ? match.time || 'TBD'
                                            : match.score || 'TBD';

                                        let elapsedDisplay = '';
                                        if (!isNotStarted) {
                                            if (match.status === 'FT') {
                                                elapsedDisplay = 'END';
                                            } else if (match.status === 'HT') {
                                                elapsedDisplay = match.elapsed;
                                            } else if (match.elapsed && Number(match.elapsed) > 0) {
                                                elapsedDisplay = `${Number(match.elapsed)}'`;
                                            } else if (match.status === 'PST') {
                                                elapsedDisplay = 'Перенесено';
                                            }
                                        }

                                        return (
                                            <button
                                                key={stableKey}
                                                onClick={() => handleClick(match)}
                                                className={`${styles.matchButton} ${demoActive && !isDemoMatchUnlocked(match) ? styles.matchButtonLocked : ''}`}
                                                disabled={demoActive && !isDemoMatchUnlocked(match)}
                                                style={{
                                                    '--match-order': Math.min(
                                                        group.animationOffset + matchIndex,
                                                        18
                                                    ),
                                                }}
                                            >
                                                {demoActive && !isDemoMatchUnlocked(match) && (
                                                    <div className={styles.matchLockBadge}>🔒</div>
                                                )}
                                                <div className={styles.matchContent}>
                                                    <div className={styles.teamInfo}>
                                                        <img
                                                            src={match.home_logo || placeholderLogo}
                                                            alt={match.home_team || 'Команда'}
                                                            style={{
                                                                width: '30px',
                                                                height: '30px',
                                                                objectFit: 'contain',
                                                            }}
                                                        />
                                                        <span>
                                                            {match.home_team || 'Невідома команда'}
                                                        </span>
                                                    </div>

                                                    <div
                                                        className={`${styles.matchCenter} ${
                                                            isNotStarted ? styles.time : styles.score
                                                        }`}
                                                    >
                                                        {elapsedDisplay && (
                                                            <div className={styles.elapsed}>
                                                                {elapsedDisplay}
                                                            </div>
                                                        )}
                                                        <span>{displayText}</span>
                                                    </div>

                                                    <div className={styles.teamInfo}>
                                                        <span>
                                                            {match.away_team || 'Неввідома команда'}
                                                        </span>
                                                        <img
                                                            src={match.away_logo || placeholderLogo}
                                                            alt={match.away_team || 'Команда'}
                                                            style={{
                                                                width: '30px',
                                                                height: '30px',
                                                                objectFit: 'contain',
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))
                    ) : (
                        <p>Немає матчів для відображення</p>
                    )}
                </div>
            </div>
        </div>
    );
}

export default FootballPage;
